// src/components/Sections/ContactPreview.jsx
import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import { catalog } from "../../data/catalog";
import "./ContactPreview.css";
import a from "../images/connect.webp";

export default function ContactPreview({ prefill = {} }) {
  const [status, setStatus] = useState("");
  const [currency, setCurrency] = useState("INR");

  // --------------------------
  // SERVICES + COURSES
  // --------------------------
  const services = useMemo(() => {
    return (catalog ?? [])
      .filter(c => c.kind === "service")
      .map((c, i) => ({ id: i + 1, name: c.title }));
  }, []);

  const courses = useMemo(() => {
    return (catalog ?? [])
      .filter(c => c.kind === "course")
      .map((c, i) => ({ id: i + 1, name: c.title }));
  }, []);

  // --------------------------
  // FORM STATE
  // --------------------------
  const initialForm = {
    name: "",
    contact: "",
    email: "",
    type: "",
    serviceOrCourseId: "",
    serviceOrCourse: "",
    budget: "",
    message: "",
    time: new Date().toLocaleString(),
  };

  const [formData, setFormData] = useState(initialForm);

  // --------------------------
  // SAFE PREFILL SUPPORT
  // --------------------------
  useEffect(() => {
    if (!prefill || !prefill.type || !prefill.serviceOrCourse) return;

    const list = prefill.type === "service" ? services : courses;

    const found = list.find(
      item =>
        item.name.toLowerCase() === prefill.serviceOrCourse.toLowerCase()
    );
    

    setFormData(prev => ({
      ...prev,
      type: prefill.type,
      serviceOrCourse: prefill.serviceOrCourse,
      serviceOrCourseId: found ? found.id : "",
    }));

    setTimeout(() => {
      document.getElementById("contact")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 200);
  }, [prefill, services, courses]);

  // --------------------------
  // INPUT CHANGE
  // --------------------------
  const handleChange = e => {
    const { name, value } = e.target;

    if (name === "type") {
      setFormData(prev => ({
        ...prev,
        type: value,
        serviceOrCourseId: "",
        serviceOrCourse: "",
        budget: "",
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSelectChange = e => {
    const { name, value } = e.target;

    const selectedLabel =
      formData.type === "service"
        ? services.find(s => s.id === Number(value))?.name
        : courses.find(c => c.id === Number(value))?.name;

    setFormData(prev => ({
      ...prev,
      [name]: value,
      serviceOrCourse: selectedLabel,
    }));
  };

  // --------------------------
  // BUDGET OPTIONS
  // --------------------------
  const budgetOptionsINR = [
    { value: "10k-20k", label: "₹10,000 – ₹20,000" },
    { value: "20k-40k", label: "₹20,000 – ₹40,000" },
    { value: "40k-80k", label: "₹40,000 – ₹80,000" },
    { value: "80k-1L", label: "₹80,000 – ₹1,00,000" },
    { value: "1L+", label: "₹1,00,000+" },
  ];

  const budgetOptionsUSD = [
    { value: "$200-$400", label: "$200 – $400" },
    { value: "$400-$800", label: "$400 – $800" },
    { value: "$800-$1500", label: "$800 – $1500" },
    { value: "$1500-$3000", label: "$1500 – $3000" },
    { value: "$3000+", label: "$3000+" },
  ];

  const budgetOptions = currency === "INR" ? budgetOptionsINR : budgetOptionsUSD;

  // --------------------------
  // SUBMIT
  // --------------------------
  const handleSubmit = e => {
    e.preventDefault();

    const templateParams = {
      name: formData.name,
      contact: formData.contact,
      email: formData.email,
      type: formData.type,
      serviceOrCourse: formData.serviceOrCourse,
      budget: formData.type === "service" ? formData.budget : "N/A",
      message: formData.message,
      time: formData.time,
    };

    emailjs
      .send(
        "ankielabs_o1",
        "template_p4erfzr",
        templateParams,
        "-RTKY5r8DPAl4jvTx"
      )
      .then(
        () => {
          setStatus("✅ Message sent successfully!");
          setFormData(initialForm);
          setTimeout(() => setStatus(""), 4000);
        },
        error => {
          console.log(error);
          setStatus("❌ Failed to send. Try again.");
          setTimeout(() => setStatus(""), 4000);
        }
      );
  };

  // --------------------------
  // ANIMATION VARIANTS
  // --------------------------
  const fadeUp = {
    hidden: { y: 30, opacity: 0 },
    show: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };

  const fade = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { duration: 0.8 } }
  };

  // --------------------------
  // UI
  // --------------------------
  return (
    <motion.section
      id="contact"
      className="contact-section"
      initial="hidden"
      whileInView="show"
      viewport={{ once: true }}
      variants={fadeUp}
    >
      <div className="contact-container">
        <motion.div
          className="contact-left"
          variants={fadeUp}
          transition={{ delay: 0.2 }}
        >
          <motion.img
            src={a}
            alt="Contact"
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8 }}
          />
        </motion.div>

        <motion.div
          className="contact-right"
          variants={fadeUp}
          transition={{ delay: 0.3 }}
        >
          <motion.h2 variants={fadeUp}>
            Get in <span>Touch</span>
          </motion.h2>

          <motion.form
            onSubmit={handleSubmit}
            initial="hidden"
            animate="show"
            variants={fade}
          >
            <motion.input
              type="text"
              name="name"
              placeholder="Full Name"
              value={formData.name}
              onChange={handleChange}
              required
              whileFocus={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 200 }}
            />

            <motion.input
              type="text"
              name="contact"
              placeholder="Contact (Optional)"
              value={formData.contact}
              onChange={handleChange}
              whileFocus={{ scale: 1.02 }}
            />

            <motion.input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
              required
              whileFocus={{ scale: 1.02 }}
            />

            <motion.select
              name="type"
              value={formData.type}
              onChange={handleChange}
              required
              whileFocus={{ scale: 1.02 }}
            >
              <option value="">Select Type</option>
              <option value="service">Service</option>
              <option value="course">Course</option>
            </motion.select>

            {formData.type && (
              <motion.select
                name="serviceOrCourseId"
                value={formData.serviceOrCourseId}
                onChange={handleSelectChange}
                required
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <option value="">
                  Select {formData.type === "service" ? "Service" : "Course"}
                </option>
                {(formData.type === "service" ? services : courses).map(
                  item => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  )
                )}
              </motion.select>
            )}

            {formData.type === "service" && (
              <>
                <motion.select
                  value={currency}
                  onChange={e => setCurrency(e.target.value)}
                  whileFocus={{ scale: 1.02 }}
                >
                  <option value="INR">₹ INR</option>
                  <option value="USD">$ USD</option>
                </motion.select>

                <motion.select
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  required
                  whileFocus={{ scale: 1.02 }}
                >
                  <option value="">Select Budget</option>
                  {budgetOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </motion.select>
              </>
            )}

            <motion.textarea
              name="message"
              placeholder="Project Details (Optional)"
              value={formData.message}
              onChange={handleChange}
              whileFocus={{ scale: 1.02 }}
            ></motion.textarea>

            <motion.button
              type="submit"
              className="btn"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Submit
            </motion.button>

            {status && (
              <motion.p
                className="status"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {status}
              </motion.p>
            )}
          </motion.form>
        </motion.div>
      </div>
    </motion.section>
  );
}
